import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, FormControlLabel, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getTenantCapabilities, updateTenantCapabilities } from "../api/capabilityApi";
import { DELEGATABLE_CAPABILITIES, apiErrorMessage, capabilityHelp, capabilityLabel } from "../utils/capabilities";

// Superadmin only (TenantList hides the tab otherwise). Toggles the delegatable capabilities of one tenant;
// non-delegatable ones (tenant.create, tenant.user.manage) never reach the UI.
export default function TenantCapabilities({ tenantId, accessToken }) {
  const { t } = useTranslation();
  const [granted, setGranted] = useState(null); // Set of capability keys, null = loading
  const [saved, setSaved] = useState([]);
  const [managed, setManaged] = useState(new Set()); // switched by the product profile, not by hand
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setGranted(null);
    getTenantCapabilities(accessToken, tenantId)
      .then((data) => {
        if (cancelled) return;
        const keys = (data.capabilities || []).filter((c) => c.granted).map((c) => c.capability);
        setGranted(new Set(keys));
        setSaved(keys);
        setManaged(new Set((data.capabilities || []).filter((c) => c.managed_by_product).map((c) => c.capability)));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(apiErrorMessage(err, t("admin.capabilities.loadFailed")));
        setGranted(new Set());
      });
    return () => { cancelled = true; };
  }, [accessToken, tenantId, t]);

  if (granted === null) return <CircularProgress size={22} sx={{ color: "var(--text-muted)" }} />;

  const toggle = (key) => {
    const next = new Set(granted);
    if (next.has(key)) next.delete(key); else next.add(key);
    setGranted(next);
  };
  const dirty = saved.length !== granted.size || saved.some((k) => !granted.has(k));

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await updateTenantCapabilities(accessToken, tenantId, Array.from(granted));
      const keys = (data.capabilities || []).filter((c) => c.granted).map((c) => c.capability);
      setGranted(new Set(keys));
      setSaved(keys);
      toast.success(t("admin.capabilities.saved"));
    } catch (err) {
      toast.error(apiErrorMessage(err, t("admin.capabilities.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "8px", p: 3, maxWidth: 560 }}>
      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t("admin.capabilities.title")}</Typography>
      <Typography variant="caption" sx={{ color: "var(--text-muted)", display: "block", mb: 2 }}>
        {t("admin.capabilities.subtitle")}
      </Typography>
      {DELEGATABLE_CAPABILITIES.map((cap) => (
        <FormControlLabel
          key={cap.key}
          sx={{ display: "flex", alignItems: "flex-start", ml: 0, mb: 1.5 }}
          control={<Switch size="small" checked={granted.has(cap.key)} onChange={() => toggle(cap.key)} disabled={saving || managed.has(cap.key)} slotProps={{ input: { "aria-label": cap.key } }} />}
          label={
            <Box sx={{ ml: 0.5 }}>
              <Typography sx={{ fontSize: 14 }}>{capabilityLabel(cap.key)}</Typography>
              <Typography variant="caption" sx={{ color: "var(--text-muted)" }}>{capabilityHelp(cap.key)}</Typography>
              {managed.has(cap.key) && (
                <Typography variant="caption" sx={{ color: "var(--text-muted)", display: "block" }}>{t("admin.capabilities.managedByProduct")}</Typography>
              )}
            </Box>
          }
        />
      ))}
      <Button size="small" variant="contained" onClick={handleSave} disabled={saving || !dirty} sx={{ mt: 1, backgroundColor: "var(--btn-primary)" }}>
        {saving ? t("admin.capabilities.saving") : t("admin.capabilities.save")}
      </Button>
    </Box>
  );
}
